<template>
  <div class="modalbox-content">
    <Loading :isActive="carregando" />

    <div class="produto-estoques__hint">
      <CIcon name="cil-info" size="sm" />
      Estoque atual por filial (somente leitura).
    </div>

    <div v-if="!carregando && !estoques.length" class="produto-estoques__empty">
      <CIcon name="cil-ban" size="lg" />
      <p>Nenhum estoque encontrado para este produto</p>
    </div>

    <GridCardRow v-else-if="estoques.length">
      <GridCard
        v-for="estoque in estoques"
        :key="estoque.id"
        :cols="12"
        :titulo="`Filial #${estoque.filial_id}`"
        :subtitulo="estoque.filial_fantasia || estoque.filial_razao_social"
        accent
      >
        <template #meta>
          <span class="sistema-badge" :class="`sistema-badge--situacao-${estoque.situacao || 'nd'}`">
            {{ situacaoLabel(estoque.situacao) }}
          </span>
          <span class="sistema-badge" :class="estoque.ativo ? 'sistema-badge--ativo' : 'sistema-badge--inativo'">
            {{ estoque.ativo ? 'Ativo' : 'Inativo' }}
          </span>
          <span class="meta-atualizacao">
            <CIcon name="cil-clock" size="sm" />
            {{ estoque.dataatualizacao | datetime }}
          </span>
        </template>

        <GridCardSection label="Quantidades">
          <div class="quantidades-grid">
            <GridCardField label="Disponível" :valor="estoque.quantidade" tipo="numero" destaque />
            <GridCardField label="Reservado" :valor="estoque.reservado" tipo="numero" />
            <GridCardField label="Bloqueado" :valor="estoque.bloqueada" tipo="numero" />
            <GridCardField label="Avaria" :valor="estoque.avaria" tipo="numero" />
            <GridCardField label="Em recebimento" :valor="estoque.quantidade_rec" tipo="numero" />
            <GridCardField label="Qtd. mínima" :valor="estoque.quantidademinima" tipo="numero" />
            <GridCardField label="Qtd. máxima" :valor="estoque.quantidademaxima" tipo="numero" />
            <GridCardField label="Qtd. máx. compl." :valor="estoque.quantidademaxima_compl" tipo="numero" />
          </div>
        </GridCardSection>

        <GridCardSection label="Comissão do representante por coluna">
          <div class="colunas-grid">
            <div class="coluna-comissao coluna-comissao--geral">
              <div class="coluna-comissao__cabecalho">Geral</div>
              <div class="coluna-comissao__valor">{{ formatarPercentual(estoque.perccomissaorep) }}</div>
            </div>
            <div v-for="coluna in 7" :key="`com-${coluna}`" class="coluna-comissao">
              <div class="coluna-comissao__cabecalho">Col. {{ coluna }}</div>
              <div class="coluna-comissao__valor">{{ formatarPercentual(estoque[`perccomissaorep${coluna}`]) }}</div>
            </div>
          </div>
        </GridCardSection>

        <GridCardSection label="Atributos comerciais">
          <div class="campos-grid">
            <GridCardField label="Custo" :valor="estoque.custo" tipo="monetary" />
            <GridCardField label="Múltiplo" :valor="estoque.multiplo" tipo="numero" />
            <GridCardField label="Múltiplo alternativo" :valor="estoque.multiplo_alternativo" tipo="numero" />
            <GridCardField label="Tipo múltiplo" :valor="estoque.tipo_multiplo" />
            <GridCardField label="Cashback" :valor="estoque.cashback" tipo="percent" />
            <GridCardField label="Prioridade estoque" :valor="estoque.prioridadeestoque" tipo="numero" />
            <GridCardField label="Giro/dia" :valor="estoque.giro_dia" tipo="numero" />
            <GridCardField label="Estoque (dias)" :valor="estoque.estoque_dias" tipo="numero" />
            <GridCardField label="Previsão recebimento" :valor="estoque.previsao_rec" tipo="date" />
            <GridCardField label="Prazo validade" :valor="estoque.prazo_validade" tipo="date" />
            <GridCardField label="Texto estoque" :valor="estoque.textoestoque" />
          </div>
        </GridCardSection>

        <GridCardSection label="Flags & canais">
          <div class="flags-grid">
            <div
              v-for="flag in flagsDoEstoque(estoque)"
              :key="flag.chave"
              class="flag"
              :class="{ 'flag--ativa': flag.ativo }"
            >
              <CIcon :name="flag.ativo ? 'cil-check-circle' : 'cil-x-circle'" size="sm" />
              {{ flag.label }}
            </div>
          </div>
        </GridCardSection>
      </GridCard>
    </GridCardRow>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";

const SITUACAO_LABELS = {
  1: "Disponível",
  2: "Reservado",
  3: "Indisponível",
  4: "Lançamento",
  5: "Promoção",
};

const FLAGS_DEFINICAO = [
  { chave: "bonificar", label: "Bonificar" },
  { chave: "lancamento", label: "Lançamento" },
  { chave: "outlet", label: "Outlet" },
  { chave: "destaque", label: "Destaque" },
  { chave: "destaque2", label: "Destaque 2" },
  { chave: "destaque3", label: "Destaque 3" },
  { chave: "destaque4", label: "Destaque 4" },
  { chave: "destaque5", label: "Destaque 5" },
  { chave: "acessob2b", label: "B2B" },
  { chave: "acessocallcenter", label: "Callcenter" },
  { chave: "acessofv", label: "Força de Vendas" },
];

export default {
  name: "ProdutoEstoques",
  components: { Loading },
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      carregando: false,
      estoques: [],
    };
  },
  watch: {
    "item.id": {
      immediate: true,
      handler(produtoId) {
        if (produtoId) this.carregar(produtoId);
      },
    },
  },
  methods: {
    carregar(produtoId) {
      this.carregando = true;
      get(`/admin/produtos/${encodeURIComponent(produtoId)}/estoques`)
        .then((json) => {
          this.estoques = json.data || [];
        })
        .catch(() => {
          this.estoques = [];
        })
        .finally(() => {
          this.carregando = false;
        });
    },
    situacaoLabel(situacao) {
      if (situacao === null || situacao === undefined) return "Não definida";
      return SITUACAO_LABELS[situacao] || `Situação ${situacao}`;
    },
    formatarPercentual(valor) {
      if (valor === null || valor === undefined || valor === "") return "—";
      return `${Number(valor).toFixed(2)}%`;
    },
    flagsDoEstoque(estoque) {
      return FLAGS_DEFINICAO.map((flag) => ({
        chave: flag.chave,
        label: flag.label,
        ativo: Number(estoque[flag.chave]) === 1,
      }));
    },
  },
};
</script>

<style scoped>
.produto-estoques__hint {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: #f9fafb;
  border-left: 3px solid #2298a3;
  border-radius: 4px;
  font-size: 13px;
  color: #475467;
  margin-bottom: 14px;
}

.produto-estoques__empty {
  text-align: center;
  padding: 40px 20px;
  color: #98a2b3;
  background: #f9fafb;
  border: 1px dashed #dddce9;
  border-radius: 10px;
}

.produto-estoques__empty p {
  margin: 8px 0 0;
  font-size: 13px;
}

.meta-atualizacao {
  display: flex;
  align-items: center;
  gap: 4px;
}

.quantidades-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
}

.campos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.colunas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 6px;
}

.coluna-comissao {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 6px;
  background: #fff;
  border: 1px solid #dddce9;
  border-radius: 6px;
  text-align: center;
}

.coluna-comissao--geral {
  background: linear-gradient(180deg, #fff, #fafaff);
  border-color: #c5e6e9;
}

.coluna-comissao__cabecalho {
  font-size: 10px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.coluna-comissao__valor {
  font-size: 13px;
  font-weight: 700;
  color: #3a3e81;
  font-feature-settings: "tnum";
}

.coluna-comissao--geral .coluna-comissao__valor {
  color: #1a7a83;
}

.flags-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 6px;
}

.flag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #f2f4f7;
  border-radius: 6px;
  font-size: 12px;
  color: #98a2b3;
}

.flag--ativa {
  background: #e8f5e9;
  border-color: #c8e6c9;
  color: #2e7d32;
  font-weight: 600;
}

.sistema-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.sistema-badge--ativo {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.sistema-badge--inativo {
  background: #f5f5f5;
  color: #999;
  border: 1px solid #e0e0e0;
}

.sistema-badge--situacao-1 { background: #e8f5e9; color: #2e7d32; border: 1px solid #c8e6c9; }
.sistema-badge--situacao-2 { background: #fff3e0; color: #e65100; border: 1px solid #ffe0b2; }
.sistema-badge--situacao-3 { background: #ffebee; color: #c62828; border: 1px solid #ffcdd2; }
.sistema-badge--situacao-4,
.sistema-badge--situacao-5 { background: #e3f2fd; color: #1565c0; border: 1px solid #bbdefb; }
.sistema-badge--situacao-nd { background: #f5f5f5; color: #999; border: 1px solid #e0e0e0; }

@media (max-width: 768px) {
  .quantidades-grid,
  .campos-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
